import React, { useEffect, useMemo, useRef } from 'react';
/**
 * CheckboxGroup
 * -------------
 * Multi-select control styled like v2 radio tiles, supporting an optional
 * inline text area when allowsFreeText is set on any option (normal or exclusive).
 *
 * Value shape:
 *   - Array of selections. Each selection is either:
 *       { optionId: string } or { optionId: string, otherText: string }
 *
 * Option Properties:
 *   - optionId (string): Unique identifier for the option
 *   - label (string): Display text for the option
 *   - helpText (string): Optional supplementary text shown below the label in italics
 *   - exclusive (boolean): If true, selecting this deselects all others and vice-versa
 *   - allowsFreeText (boolean): If true, shows a textarea for additional details when selected
 *   - freeTextPlaceholder (string): Custom placeholder for the free text textarea
 *
 * Auto-focus behaviour:
 *   When any option with allowsFreeText transitions from unselected to selected, the
 *   textarea is automatically focused. This uses the same ref-map + shouldFocus flag
 *   pattern as RadioGroup for consistency across the two sibling components.
 *
 * UX:
 *   - Smooth visual transitions between selected/unselected.
 *   - Required asterisk on the label when required.
 * Accessibility:
 *   - Uses role="group" with labelledby; tiles act as checkboxes via buttons.
 */
export default function CheckboxGroup({ name, label, options = [], required = false, value, onChange }) {
  const selections = useMemo(() => {
    if (Array.isArray(value)) return value;
    return [];
  }, [value]);

  const isSelected = (optId) => {
    return selections.some((s) => (s?.optionId ?? s) === optId);
  };

  const isExclusive = (optId) => {
    const opt = (options || []).find(o => o.optionId === optId);
    return !!(opt && opt.exclusive === true);
  };

  // Ref map keyed by optionId for textarea elements (mirrors RadioGroup pattern).
  // Populated via callback refs on each textarea so any allowsFreeText option can
  // be auto-focused when it transitions to selected.
  const freeTextRefs = useRef({});
  // Flag set at selection time inside toggleOption; the effect below picks it up
  // on the next render to focus the newly-visible textarea.
  const shouldFocusFreeTextRef = useRef(false);

  const toggleOption = (opt) => {
    const optId = opt.optionId;
    const exists = selections.findIndex((s) => (s?.optionId ?? s) === optId);

    if (isExclusive(optId)) {
      if (exists >= 0) {
        const next = selections.slice();
        next.splice(exists, 1);
        onChange(next);
      } else {
        // Select ONLY this exclusive option, clearing all others.
        // Include otherText when the option accepts free text so the textarea
        // value is initialised and validation can track it.
        if (opt.allowsFreeText) shouldFocusFreeTextRef.current = true;
        const entry = opt.allowsFreeText
          ? { optionId: optId, otherText: '' }
          : { optionId: optId };
        onChange([entry]);
      }
      return;
    }

    // Non-exclusive: deselect if already selected
    if (exists >= 0) {
      const next = selections.slice();
      next.splice(exists, 1);
      onChange(next);
      return;
    }

    // Add non-exclusive; remove any exclusive option currently selected.
    // Include otherText field for options that accept free text.
    if (opt.allowsFreeText) shouldFocusFreeTextRef.current = true;
    const entry = opt.allowsFreeText ? { optionId: optId, otherText: '' } : { optionId: optId };
    const withoutExclusive = selections.filter((s) => !isExclusive(s?.optionId ?? s));
    onChange([...withoutExclusive, entry]);
  };

  const onFreeTextChange = (optId, text) => {
    const idx = selections.findIndex((s) => (s?.optionId ?? s) === optId);
    if (idx < 0) return;
    const next = selections.slice();
    const cur = next[idx] || { optionId: optId };
    next[idx] = { ...cur, otherText: text };
    onChange(next);
  };

  const normalOptions = (options || []).filter(o => !o.exclusive);
  const exclusiveOptions = (options || []).filter(o => o.exclusive === true);

  // Auto-focus: after render, if the shouldFocus flag was set during toggleOption,
  // find the newly-selected allowsFreeText option and focus its textarea.
  // This mirrors RadioGroup's proven pattern so both components behave identically.
  useEffect(() => {
    if (!shouldFocusFreeTextRef.current) return;
    const allOpts = [...normalOptions, ...exclusiveOptions];
    const target = allOpts.find(
      (o) => o.allowsFreeText && isSelected(o.optionId)
    );
    if (!target) return;
    const el = freeTextRefs.current[target.optionId];
    if (el && typeof el.focus === 'function') {
      try {
        el.focus({ preventScroll: true });
        const len = (el.value || '').length;
        if (typeof el.setSelectionRange === 'function') {
          el.setSelectionRange(len, len);
        }
      } catch (_) {}
    }
    shouldFocusFreeTextRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selections]);

  /**
   * Renders a single option tile (checkbox icon, label, helpText, and optional
   * free-text textarea). Used by both normalOptions and exclusiveOptions so the
   * two rendering paths can never drift apart.
   */
  const renderOptionTile = (opt) => {
    const selected = isSelected(opt.optionId);
    const onTileClick = () => toggleOption(opt);
    // Keyboard support (TD-17): Space/Enter toggles, matching the click handler.
    const onTileKeyDown = (e) => {
      // Ignore keys bubbling up from the nested free-text field, otherwise
      // Space/Enter would be swallowed (preventDefault) before reaching the
      // textarea and words would run together (TD-128).
      if (e.target !== e.currentTarget) return;
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        onTileClick();
      }
    };
    return (
      <div
        key={opt.optionId}
        className={`group p-3 rounded-xl border-2 cursor-pointer transition-all duration-200 ease-out focus:outline-none focus:ring-2 focus:ring-primary ${selected ? 'border-primary bg-primary/5 shadow-sm' : 'border-gray-200 bg-white hover:border-gray-300'}`}
        onClick={onTileClick}
        onKeyDown={onTileKeyDown}
        tabIndex={0}
        role="checkbox"
        aria-checked={selected}
      >
        <div className="flex items-start gap-3">
          <span
            className={`flex-shrink-0 w-5 h-5 mt-0.5 rounded-md border-2 transition-all duration-200 ease-out relative ${selected ? 'bg-yellow-400 border-yellow-400' : 'border-gray-300 bg-white group-hover:border-gray-400'}`}
            aria-hidden="true"
          >
            {selected && (
              <svg viewBox="0 0 16 16" className="absolute inset-0 w-3.5 h-3.5 m-auto fill-white">
                <path d="M6.173 12.414a1 1 0 0 1-1.414 0L1.88 9.535a1 1 0 1 1 1.415-1.415l2.18 2.18 7.23-7.23a1 1 0 1 1 1.415 1.414l-8.947 8.93z"/>
              </svg>
            )}
          </span>
          <div className="flex-1 min-w-0">
            <span className="text-sm font-medium">{opt.label}</span>
            {opt.helpText && (
              <p className="text-xs italic mt-0.5 text-gray-500">{opt.helpText}</p>
            )}
          </div>
        </div>
        {opt.allowsFreeText && selected && (
          <div
            className="transition-all duration-300 overflow-hidden max-h-28 mt-2"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <textarea
              ref={(el) => { freeTextRefs.current[opt.optionId] = el; }}
              className="w-[calc(100%-theme(spacing.1)*2)] mx-1 mt-1 p-2 border rounded-lg bg-white text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-primary border-gray-200 box-border"
              placeholder={opt.freeTextPlaceholder || opt.placeholder || "Please specify"}
              value={selections.find((s) => (s?.optionId ?? s) === opt.optionId)?.otherText || ''}
              onChange={(e) => onFreeTextChange(opt.optionId, e.target.value)}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              rows={2}
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="mb-4">
      <div id={`${name}-label`} className="text-sm font-medium text-gray-700 mb-2">
        {label}{required ? <span className="text-red-600"> *</span> : null}
      </div>
      <div className="space-y-2" role="group" aria-labelledby={`${name}-label`}>
        {normalOptions.map(renderOptionTile)}
        {exclusiveOptions.length > 0 && (
          <>
            <div className="flex items-center my-3">
              <div className="flex-1 h-px bg-gray-200" />
              <div className="px-2 text-xs text-gray-500">or</div>
              <div className="flex-1 h-px bg-gray-200" />
            </div>
            {exclusiveOptions.map(renderOptionTile)}
          </>
        )}
      </div>
    </div>
  );
}
